import { useState } from 'react';
import type { BbgTerminalUsage, BbgUsageMetric, BbgUsageResponse } from '@shared/contract';
import { duration } from '@/lib/format';
import {
  USAGE_METRICS,
  average,
  blockKind,
  count,
  dayLabel,
  eventBaseline,
  eventTime,
  num,
  ratio,
  ratioOf,
  surgeThreshold,
  terminalEntries,
  terminalSurging,
} from '@/lib/bbgUsage';
import { StatusChip } from '@/components/chips';
import { HatchPattern, UsageChart } from '@/components/UsageChart';
import { useBbgUsage, type BbgUsageState } from '@/hooks/useBbgUsage';

const USAGE_DAYS = 60;

export function BloombergView() {
  const state = useBbgUsage(USAGE_DAYS);
  return <BbgUsagePanel {...state} />;
}

/** Stateless over the fetched data, so it renders the same in tests as live. */
export function BbgUsagePanel({ data, error, loading, updatedAt }: BbgUsageState) {
  const [metric, setMetric] = useState<BbgUsageMetric>('new_securities');

  if (!data) {
    return (
      <section className="view is-active" aria-label="Bloomberg">
        <div className="empty">
          <strong>{loading ? 'Loading usage history…' : 'Usage history unavailable'}</strong>
          {!loading && error && <code>{error}</code>}
        </div>
      </section>
    );
  }

  if (!data.enabled) {
    return (
      <section className="view is-active" aria-label="Bloomberg">
        <div className="empty">
          <strong>Usage tracking disabled</strong>
        </div>
      </section>
    );
  }

  const terminals = terminalEntries(data);
  const events = Array.isArray(data.events) ? data.events : [];

  return (
    <section className="view is-active" aria-label="Bloomberg">
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Metric">
          {USAGE_METRICS.map(({ id, label }) => (
            <button
              key={id}
              className={`seg${metric === id ? ' is-active' : ''}`}
              aria-pressed={metric === id}
              onClick={() => setMetric(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <UsageLegend />
        <span className="usage-meta">
          {data.since && <span>since {dayLabel(data.since)}</span>}
          {error ? (
            <span className="st-degraded">stale · {error}</span>
          ) : (
            updatedAt && <span>updated {updatedAt.toLocaleTimeString()}</span>
          )}
        </span>
      </div>

      {terminals.length === 0 ? (
        <div className="empty usage-empty">
          <strong>No terminals reported</strong>
        </div>
      ) : (
        <div className="usage-grid">
          {terminals.map(([id, terminal]) => (
            <TerminalUsage key={id} id={id} terminal={terminal} usage={data} metric={metric} />
          ))}
        </div>
      )}

      <section className="group">
        <div className="group__head">
          <h2 className="group__title">Blocks</h2>
          <span className="group__rule" />
          <span className="muted group__count">{events.length}</span>
        </div>
        {events.length === 0 ? (
          <div className="empty">
            <strong>No blocks recorded</strong>
          </div>
        ) : (
          <div className="panel">
            <div className="table-wrap">
              <table className="usage-events">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Terminal</th>
                    <th>Kind</th>
                    <th>Detail</th>
                    <th>New securities</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, i) => {
                    const kind = blockKind(event.kind);
                    const { today, avg } = eventBaseline(event);
                    return (
                      <tr key={`${event.ts}-${event.worker_id}-${i}`}>
                        <td className="nowrap">
                          {eventTime(event.ts)}
                          <span className="muted"> · {duration(event.ts)} ago</span>
                        </td>
                        <td className="nowrap">
                          <code>{event.worker_id}</code>
                        </td>
                        <td>
                          <StatusChip status={kind.status} label={kind.label} />
                        </td>
                        <td className="usage-events__detail">{event.detail}</td>
                        <td className="nowrap">
                          <strong>{count(today)}</strong>
                          <span className="muted">
                            {' '}
                            / avg {average(avg)} · {ratio(ratioOf(today, avg))}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </section>
  );
}

function TerminalUsage({
  id,
  terminal,
  usage,
  metric,
}: {
  id: string;
  terminal: BbgTerminalUsage;
  usage: BbgUsageResponse;
  metric: BbgUsageMetric;
}) {
  const surging = terminalSurging(terminal);
  const surgingMetrics = new Set(terminal.surging ?? []);
  const counted = num(terminal.baseline_days_counted);
  const windowDays = num(usage.baseline_days);
  const avgFor = (m: BbgUsageMetric) => num(terminal.baseline_avg?.[m]);

  return (
    <article className={`usage${surging ? ' is-surge' : ''}`} aria-label={id}>
      <div className="usage__head">
        <span className="usage__name">{id}</span>
        {surging ? (
          <span className="chip chip--down">
            <span className="chip__glyph" aria-hidden="true">
              ▲
            </span>
            surge
          </span>
        ) : (
          <StatusChip status="ok" label="normal" />
        )}
        <span className="usage__base">
          baseline{' '}
          {counted != null && windowDays != null && counted < windowDays
            ? `${counted}/${windowDays}`
            : (windowDays ?? counted ?? '—')}
          d
        </span>
      </div>

      <div className="usage__tiles">
        {USAGE_METRICS.map(({ id: m, label }) => {
          const hot = surgingMetrics.has(m);
          const avg = avgFor(m);
          return (
            <div key={m} className={`usage-tile${hot ? ' is-surge' : ''}`}>
              <div className="usage-tile__label">
                {label}
                {hot && (
                  <span className="usage-tile__flag">
                    {' '}
                    <span aria-hidden="true">▲</span> surge
                  </span>
                )}
              </div>
              <div className="usage-tile__value">{count(num(terminal.today?.[m]))}</div>
              <div className="usage-tile__sub">
                <span className="usage-tile__ratio">{ratio(num(terminal.ratio?.[m]))}</span> of avg{' '}
                {average(avg)}
              </div>
              <div className="usage-tile__sub">surge at {average(surgeThreshold(usage, avg, m))}</div>
            </div>
          );
        })}
      </div>

      <UsageChart
        terminalId={id}
        metric={metric}
        days={Array.isArray(terminal.days) ? terminal.days : []}
        todayDate={terminal.today?.date ?? null}
        baseline={avgFor(metric)}
        threshold={surgeThreshold(usage, avgFor(metric), metric)}
        surging={surgingMetrics.has(metric)}
      />
    </article>
  );
}

function UsageLegend() {
  return (
    <ul className="usage-legend" aria-label="Legend">
      <li>
        <svg viewBox="0 0 14 10" aria-hidden="true">
          <rect className="uc-bar" x="2" y="1" width="10" height="9" rx="2" />
        </svg>
        Daily
      </li>
      <li>
        <svg viewBox="0 0 14 10" aria-hidden="true">
          <rect className="uc-bar is-today" x="2" y="1" width="10" height="9" rx="2" />
        </svg>
        Today
      </li>
      <li>
        <svg viewBox="0 0 14 10" aria-hidden="true">
          <line className="uc-avg" x1="0" x2="14" y1="5" y2="5" />
        </svg>
        Baseline avg
      </li>
      <li>
        <svg viewBox="0 0 14 10" aria-hidden="true">
          <line className="uc-surge" x1="0" x2="14" y1="5" y2="5" />
        </svg>
        Surge threshold
      </li>
      <li>
        <svg viewBox="0 0 14 10" aria-hidden="true">
          <defs>
            <HatchPattern id="uc-legend-hatch" />
          </defs>
          <rect x="0" y="0" width="14" height="10" fill="url(#uc-legend-hatch)" className="uc-legend-unknown" />
        </svg>
        No data
      </li>
    </ul>
  );
}
